'use client';

import Script from 'next/script';
import { useEffect } from 'react';

import { LOCALES } from '@commons/i18n';

type AccessWidgetLanguage = 'en' | 'fr' | 'ja';

type WindowWithAcsb = Window & {
  acsbJS?: {
    init: () => void;
  };
  accessWidget?: {
    setLanguage?: (language: AccessWidgetLanguage) => void;
  };
  accessWidgetOptions?: Record<string, unknown> & {
    language?: AccessWidgetLanguage;
  };
};

declare const window: WindowWithAcsb;

const ACCESS_WIDGET_LANGUAGES = {
  [LOCALES.EN]: 'en',
  [LOCALES.FR]: 'fr',
  [LOCALES.JA]: 'ja',
} as const satisfies Record<LOCALES, AccessWidgetLanguage>;

/**
 * Resolve a site locale to an accessiBe interface language.
 *
 * @param locale - The current validated Next.js route locale.
 *
 * @returns The corresponding accessiBe language, defaulting to English.
 */

const getAccessWidgetLanguage = (locale: LOCALES): AccessWidgetLanguage => {
  return ACCESS_WIDGET_LANGUAGES[locale] ?? ACCESS_WIDGET_LANGUAGES[LOCALES.EN];
};

/**
 * Synchronize the configured and loaded accessiBe widget language.
 *
 * @param locale - The current validated Next.js route locale.
 */

const syncAccessWidgetLanguage = (locale: LOCALES) => {
  const language = getAccessWidgetLanguage(locale);

  window.accessWidgetOptions = {
    ...window.accessWidgetOptions,
    language,
  };

  window.accessWidget?.setLanguage?.(language);
};

type AccessiBEWidgetProps = {
  locale: LOCALES;
};

/**
 * AccessiBE accessibility widget script.
 *
 * Loads the widget script and keeps its interface language synchronized with the route locale.
 */

const AccessiBEWidget = ({ locale }: AccessiBEWidgetProps) => {
  useEffect(() => {
    syncAccessWidgetLanguage(locale);
  }, [locale]);

  return (
    <Script
      id="accessibility-widget"
      src="https://acsbapp.com/apps/app/dist/js/app.js"
      strategy="afterInteractive"
      onLoad={() => {
        syncAccessWidgetLanguage(locale);
        window.acsbJS?.init();
        syncAccessWidgetLanguage(locale);
      }}
    />
  );
};

export default AccessiBEWidget;
